import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Position, Company } from '../../types';
import { useNavigate } from 'react-router-dom';
import airtableService from '../../services/airtable';

const PositionsList: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [positionsResponse, companiesResponse] = await Promise.all([
        airtableService.getPositions(),
        airtableService.getCompanies()
      ]);

      if (positionsResponse.success) {
        setPositions(positionsResponse.data);
      } else {
        setError(positionsResponse.error || 'Failed to fetch positions');
      }

      if (companiesResponse.success) {
        setCompanies(companiesResponse.data);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown Company';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return 'success';
      case 'On Hold': return 'warning';
      case 'Filled': return 'primary';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const filteredPositions = positions.filter(position => {
    const matchesSearch = `${position.title} ${getCompanyName(position.companyId)} ${position.department}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || position.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setFilterAnchor(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="h6">
          Unable to load positions. Please check your Airtable configuration.
        </Typography>
        <Button variant="outlined" onClick={fetchData} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Positions ({filteredPositions.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/positions/new')}
        >
          Add Position
        </Button>
      </Box>

      {/* Search and Filter */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder="Search positions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <IconButton onClick={handleFilterClick}>
          <FilterIcon />
        </IconButton>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleStatusFilter('')}>All Status</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Open')}>Open</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('On Hold')}>On Hold</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Filled')}>Filled</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Cancelled')}>Cancelled</MenuItem>
        </Menu>
      </Box>

      {/* Positions Grid */}
      <Grid container spacing={3}>
        {filteredPositions.map((position) => (
          <Grid item xs={12} md={6} lg={4} key={position.id} sx={{ display: 'flex' }}>
            <Card
              elevation={2}
              sx={{
                cursor: 'pointer',
                '&:hover': { elevation: 4 },
                transition: 'box-shadow 0.2s',
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => navigate(`/positions/${position.id}`)}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box flexGrow={1}>
                    <Typography variant="h6" gutterBottom>
                      {position.title}
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {getCompanyName(position.companyId)}
                      </Typography>
                    </Box>
                    {position.department && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {position.department}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="end" gap={1}>
                    <Chip
                      label={position.status}
                      color={getStatusColor(position.status) as any}
                      size="small"
                    />
                    <Chip
                      label={position.priority}
                      color={getPriorityColor(position.priority) as any}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={1} mb={2} sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center">
                    <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {position.location} • {position.remoteWork}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {position.employmentType}
                    </Typography>
                  </Box>

                  {(position.salaryRangeMin || position.salaryRangeMax) && (
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {position.salaryRangeMin && position.salaryRangeMax
                          ? `$${position.salaryRangeMin.toLocaleString()} - $${position.salaryRangeMax.toLocaleString()}`
                          : position.salaryRangeMin
                          ? `$${position.salaryRangeMin.toLocaleString()}+`
                          : `Up to $${position.salaryRangeMax?.toLocaleString()}`
                        } {position.currency}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center">
                    <GroupIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {position.hiringManager}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 'auto' }}>
                  {position.deadline && (
                    <Box display="flex" alignItems="center">
                      <ScheduleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Deadline: {position.deadline.toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Posted {position.createdDate.toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredPositions.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No positions found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or add a new position
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PositionsList;